import { Activity, Radio } from "lucide-react";
import { Reveal } from "./Reveal";
import { useMarketData, MARKET_STATS } from "@/hooks/useMarketData";
import { useLanguage } from "@/i18n/LanguageContext";
import { CA } from "@/config";

export const LiveChart = () => {
  const { mode, data: live } = useMarketData();
  const isLive = mode === "live";
  const { t } = useLanguage();

  return (
    <section id="chart" className="scroll-mt-24 px-5 pb-20 sm:px-8">
      <Reveal className="mx-auto max-w-7xl">
        <div
          data-testid="chart-container"
          className="overflow-hidden rounded-3xl border border-purple-500/20 bg-[#0E0E16]/90 shadow-[0_0_60px_rgba(153,69,255,0.1)]"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-500/15 px-5 py-4 sm:px-6">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <span className="flex items-center gap-2 font-display text-base font-bold text-[#F5F3FF] sm:text-lg">
                <Activity size={18} className="text-[#14F195]" />
                {t("chart.pair")}
              </span>
              {isLive ? (
                <span
                  data-testid="chart-mode-badge"
                  className="font-display rounded-full border border-[#14F195]/40 bg-[#14F195]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#14F195]"
                >
                  {t("chart.live")}
                </span>
              ) : (
                <span
                  data-testid="chart-mode-badge"
                  className="font-display inline-flex items-center gap-1.5 rounded-full border border-purple-500/40 bg-[#14141E] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-purple-300"
                >
                  <Radio size={11} />
                  {t("chart.notActiveBadge")}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-px bg-purple-500/10 sm:grid-cols-6" data-testid="market-stats-bar">
            {MARKET_STATS.map((s) => {
              const v = isLive && live ? live[s.key] : null;
              return (
                <div
                  key={s.key}
                  data-testid={`market-stat-${s.key}`}
                  className="bg-[#0E0E16] px-4 py-3"
                >
                  <div className="font-display text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500">
                    {t(`stats.${s.key}`)}
                  </div>
                  <div
                    className={`font-display mt-1 text-base font-extrabold ${
                      s.key === "change24h" && v
                        ? live.changeUp
                          ? "text-[#14F195]"
                          : "text-red-400"
                        : "text-[#F5F3FF]"
                    }`}
                  >
                    {v || "—"}
                  </div>
                </div>
              );
            })}
          </div>

          {isLive ? (
            <iframe
              data-testid="chart-embed"
              src={`https://dexscreener.com/solana/${CA}?embed=1&theme=dark&info=1`}
              className="h-[420px] w-full border-0"
              title="SOLMOO live market chart"
            />
          ) : (
            <div
              data-testid="chart-inactive"
              className="flex h-[320px] flex-col items-center justify-center gap-3 border-t border-purple-500/10 px-6 text-center sm:h-[400px]"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#14141E]">
                <Radio size={24} className="text-[#14F195]" />
              </span>
              <span className="font-display text-flow text-2xl font-extrabold sm:text-3xl">
                {t("chart.notActiveBig")}
              </span>
              <p className="max-w-md text-sm leading-relaxed text-gray-400">
                {t("chart.notActiveBody")}
              </p>
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
};
